import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Input,
  message,
  Modal,
  Segmented,
  Select,
  Space,
  theme,
  Typography,
  Upload,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import {
  IReleaseNote,
  IShareReleaseNotePayload,
  MerchantShareMode,
  ReleaseNoteAudience,
  ReleaseNoteLanguage,
  ReleaseNoteMerchantRole,
} from "../../../../interfaces/releaseNote.interface";
import { shareReleaseNoteServer } from "../../releaseNotesApi";
import {
  AUDIENCE_LABELS,
  AUDIENCE_ORDER,
  LANGUAGE_LABELS,
  LANGUAGE_ORDER,
  MERCHANT_ROLE_LABELS,
  MERCHANT_ROLE_ORDER,
} from "../../releaseNotesHelpers";

const { Text, Paragraph } = Typography;

interface ShareModalProps {
  open: boolean;
  releaseNote: IReleaseNote | null;
  onClose: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseEmails = (text: string): string[] => {
  const parts = text.split(/[\s,;]+/).map((p) => p.trim().toLowerCase());
  return Array.from(new Set(parts.filter((p) => EMAIL_REGEX.test(p))));
};

const ShareModal: React.FC<ShareModalProps> = ({
  open,
  releaseNote,
  onClose,
}) => {
  const { token } = theme.useToken();

  const [partnersEnabled, setPartnersEnabled] = useState(false);
  const [partnersText, setPartnersText] = useState("");
  const [partnersLanguage, setPartnersLanguage] = useState<ReleaseNoteLanguage>(
    ReleaseNoteLanguage.English
  );
  const [merchantsEnabled, setMerchantsEnabled] = useState(false);
  const [merchantMode, setMerchantMode] = useState<MerchantShareMode>(
    MerchantShareMode.Resolve
  );
  const [merchantLocales, setMerchantLocales] = useState<ReleaseNoteLanguage[]>(
    [ReleaseNoteLanguage.English]
  );
  const [merchantRoles, setMerchantRoles] = useState<ReleaseNoteMerchantRole[]>(
    [...MERCHANT_ROLE_ORDER]
  );
  const [merchantManualLanguage, setMerchantManualLanguage] =
    useState<ReleaseNoteLanguage>(ReleaseNoteLanguage.English);
  const [merchantManualText, setMerchantManualText] = useState("");
  const [slackEnabled, setSlackEnabled] = useState(false);
  const [slackAudience, setSlackAudience] = useState<ReleaseNoteAudience>(
    ReleaseNoteAudience.Internal
  );
  const [slackLanguage, setSlackLanguage] = useState<ReleaseNoteLanguage>(
    ReleaseNoteLanguage.English
  );
  const [submitting, setSubmitting] = useState(false);

  const partnerEmails = useMemo(
    () => parseEmails(partnersText),
    [partnersText]
  );
  const merchantManualEmails = useMemo(
    () => parseEmails(merchantManualText),
    [merchantManualText]
  );

  const buildMerchants = ():
    | NonNullable<NonNullable<IShareReleaseNotePayload["email"]>["merchants"]>
    | undefined => {
    if (!merchantsEnabled) return undefined;
    if (merchantMode === MerchantShareMode.Manual) {
      if (merchantManualEmails.length === 0) return undefined;
      return {
        mode: MerchantShareMode.Manual,
        locales: [merchantManualLanguage],
        recipients: merchantManualEmails,
      };
    }
    if (merchantLocales.length === 0 || merchantRoles.length === 0) {
      return undefined;
    }
    return {
      mode: MerchantShareMode.Resolve,
      locales: merchantLocales,
      roles: merchantRoles,
    };
  };

  const buildPayload = (): IShareReleaseNotePayload | null => {
    const payload: IShareReleaseNotePayload = {};
    const email: NonNullable<IShareReleaseNotePayload["email"]> = {};
    if (partnersEnabled && partnerEmails.length > 0) {
      email.partners = {
        recipients: partnerEmails,
        language: partnersLanguage,
      };
    }
    const merchants = buildMerchants();
    if (merchants) email.merchants = merchants;
    if (email.partners || email.merchants) payload.email = email;
    if (slackEnabled) {
      payload.slack = { audience: slackAudience, language: slackLanguage };
    }
    return payload.email || payload.slack ? payload : null;
  };

  const handleShare = async () => {
    if (!releaseNote) return;
    const payload = buildPayload();
    if (!payload) {
      message.warning("Pick at least one channel to share.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await shareReleaseNoteServer({
        id: releaseNote._id,
        payload,
      });
      const merchantResults = result.merchants ?? [];
      const merchantsSent = merchantResults.reduce((sum, m) => sum + m.sent, 0);
      const parts: string[] = [];
      if (result.partners) parts.push(`${result.partners.sent} partner(s)`);
      if (merchantResults.length) parts.push(`${merchantsSent} merchant(s)`);
      if (result.slack?.posted) parts.push("Slack");
      if (parts.length) message.success(`Shared: ${parts.join(", ")}.`);
      if (result.errors.length) message.warning(result.errors.join(" "));
      if (!parts.length && !result.errors.length) {
        message.info("Nothing was sent.");
      }
      onClose();
    } catch (err) {
      message.error("Failed to share the release note.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Share Release Note"
      open={open}
      onCancel={onClose}
      okText="Share Now"
      okButtonProps={{ loading: submitting }}
      onOk={handleShare}
      width={560}
    >
      <Paragraph type="secondary">
        Distribute this release note by email and/or Slack.
      </Paragraph>

      <Checkbox
        checked={partnersEnabled}
        onChange={(e) => setPartnersEnabled(e.target.checked)}
      >
        Email partners
      </Checkbox>
      {partnersEnabled && (
        <Space
          direction="vertical"
          style={{ width: "100%", marginTop: token.marginXS }}
        >
          <div>
            <Text type="secondary">Content language</Text>
            <Select
              style={{ width: "100%", marginTop: 4 }}
              value={partnersLanguage}
              onChange={setPartnersLanguage}
              options={LANGUAGE_ORDER.map((l) => ({
                label: LANGUAGE_LABELS[l],
                value: l,
              }))}
            />
          </div>
          <Input.TextArea
            rows={3}
            placeholder="Paste partner emails (comma, space or newline separated)"
            value={partnersText}
            onChange={(e) => setPartnersText(e.target.value)}
          />
          <Space>
            <Upload
              accept=".csv,.txt"
              showUploadList={false}
              beforeUpload={(file) => {
                file
                  .text()
                  .then((content) =>
                    setPartnersText((prev) =>
                      prev ? `${prev}\n${content}` : content
                    )
                  );
                return false;
              }}
            >
              <Button size="small" icon={<UploadOutlined />}>
                Upload CSV
              </Button>
            </Upload>
            <Text type="secondary">{partnerEmails.length} valid email(s)</Text>
          </Space>
        </Space>
      )}

      <Divider style={{ margin: `${token.marginSM}px 0` }} />

      <Checkbox
        checked={merchantsEnabled}
        onChange={(e) => setMerchantsEnabled(e.target.checked)}
      >
        Email merchants
      </Checkbox>
      {merchantsEnabled && (
        <Space
          direction="vertical"
          style={{ width: "100%", marginTop: token.marginXS }}
        >
          <Segmented
            block
            value={merchantMode}
            onChange={(value) => setMerchantMode(value as MerchantShareMode)}
            options={[
              { label: "From database", value: MerchantShareMode.Resolve },
              { label: "Manual list", value: MerchantShareMode.Manual },
            ]}
          />
          {merchantMode === MerchantShareMode.Resolve ? (
            <>
              <div>
                <Text type="secondary">
                  Languages (locale resolves country)
                </Text>
                <Select
                  mode="multiple"
                  style={{ width: "100%", marginTop: 4 }}
                  value={merchantLocales}
                  onChange={setMerchantLocales}
                  options={LANGUAGE_ORDER.map((l) => ({
                    label: LANGUAGE_LABELS[l],
                    value: l,
                  }))}
                />
              </div>
              <div>
                <Text type="secondary">Merchant roles</Text>
                <Select
                  mode="multiple"
                  style={{ width: "100%", marginTop: 4 }}
                  value={merchantRoles}
                  onChange={setMerchantRoles}
                  options={MERCHANT_ROLE_ORDER.map((r) => ({
                    label: MERCHANT_ROLE_LABELS[r],
                    value: r,
                  }))}
                />
              </div>
              <Alert
                type="info"
                showIcon
                message="Dutch → NL, Italian → IT, English → all other countries."
              />
            </>
          ) : (
            <>
              <div>
                <Text type="secondary">Content language</Text>
                <Select
                  style={{ width: "100%", marginTop: 4 }}
                  value={merchantManualLanguage}
                  onChange={setMerchantManualLanguage}
                  options={LANGUAGE_ORDER.map((l) => ({
                    label: LANGUAGE_LABELS[l],
                    value: l,
                  }))}
                />
              </div>
              <Input.TextArea
                rows={3}
                placeholder="Paste merchant emails (comma, space or newline separated)"
                value={merchantManualText}
                onChange={(e) => setMerchantManualText(e.target.value)}
              />
              <Space>
                <Upload
                  accept=".csv,.txt"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    file
                      .text()
                      .then((content) =>
                        setMerchantManualText((prev) =>
                          prev ? `${prev}\n${content}` : content
                        )
                      );
                    return false;
                  }}
                >
                  <Button size="small" icon={<UploadOutlined />}>
                    Upload CSV
                  </Button>
                </Upload>
                <Text type="secondary">
                  {merchantManualEmails.length} valid email(s)
                </Text>
              </Space>
            </>
          )}
        </Space>
      )}

      <Divider style={{ margin: `${token.marginSM}px 0` }} />

      <Checkbox
        checked={slackEnabled}
        onChange={(e) => setSlackEnabled(e.target.checked)}
      >
        Post to Slack
      </Checkbox>
      {slackEnabled && (
        <Space style={{ marginTop: token.marginXS }}>
          <Select
            style={{ width: 160 }}
            value={slackAudience}
            onChange={setSlackAudience}
            options={AUDIENCE_ORDER.map((a) => ({
              label: AUDIENCE_LABELS[a],
              value: a,
            }))}
          />
          <Select
            style={{ width: 120 }}
            value={slackLanguage}
            onChange={setSlackLanguage}
            options={LANGUAGE_ORDER.map((l) => ({
              label: LANGUAGE_LABELS[l],
              value: l,
            }))}
          />
        </Space>
      )}
    </Modal>
  );
};

export default ShareModal;
